import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import {
    ArrowLeft,
    CheckCircle,
    XCircle,
    Plus,
    Edit,
    Target,
    Building2,
    FileText,
    Info,
    Calendar,
    Timer,
    Sunrise,
    Sun,
    Sunset,
    Image,
    Hand,
    AlertCircle,
    Clock
} from 'lucide-react';
import { checkpointService } from '../../../services/checkpointService';
import { toast } from 'react-hot-toast';
import Button from '../../../shared/components/ui/button/Button';
import {
    getDefaultBufferTime,
    getBufferTimeOptions,
    getMaxBufferTime,
    validateCheckpointName,
    validateDescription,
    validateRequirement
} from '../../checkpoint/utils/checkpointUtils';

interface ProjectAssetCheckpointDrawerProps {
    open: boolean;
    onClose: () => void;
    projectAsset: {
        _id: string;
        name: string;
    } | null;
    onRefresh?: () => void;
}

interface Checkpoint {
    _id: string;
    id?: string;
    name: string;
    frequency: 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly';
    status: 'active' | 'inactive';
    approvalStatus?: 'pending' | 'approved' | 'rejected' | 'modified';
    approverComments?: string;
    requirement?: string;
    description?: string;
    taskType?: ('Visual' | 'Hearing' | 'Manual')[];
    isImageRequired?: boolean;
    isInputRequired?: 'None' | 'Number' | 'Text' | 'Both';
    executionPhase?: string;
    executionTime?: string;
    bufferTimeDays?: number;
    createdAt: string;
    updatedAt: string;
    createdBy?: { name: string };
    updatedBy?: { name: string };
}

const FREQUENCY_OPTIONS = ['Daily', 'Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Half-Yearly', 'Yearly'];

// Frequency color mapping matching checkpoint master
const FREQUENCY_COLORS: Record<string, { bg: string; text: string; border: string; dot: string }> = {
    Daily: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
    Weekly: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-500' },
    Fortnightly: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
    Monthly: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
    Quarterly: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
    'Half-Yearly': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
    Yearly: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', dot: 'bg-indigo-500' },
};

const formatRelativeTime = (dateString: string | Date) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
    return `${Math.floor(diffInDays / 365)} years ago`;
};

// Form state type for checkpoint add/edit
export interface CheckpointFormState {
    name: string;
    frequency: 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly';
    description: string;
    requirement: string;
    taskType: ('Visual' | 'Hearing' | 'Manual')[];
    isImageRequired: boolean;
    isInputRequired: 'None' | 'Number' | 'Text' | 'Both';
    executionPhase: string;
    executionTime: string;
    bufferTimeDays: number;
    status: 'active' | 'inactive';
}

// Extracted form component - defined at module level to prevent remount on every keystroke
// (defining inside parent caused input focus loss after first character)
const SHIFT_OPTIONS = [
    { id: 'Morning', label: 'Pre-Gen (AM)', time: '05:00-09:00', icon: Sunrise, color: 'bg-orange-50 text-orange-600 border-orange-200' },
    { id: 'Standard', label: 'Standard (Day)', time: '09:00-18:00', icon: Sun, color: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
    { id: 'Evening', label: 'Post-Gen (PM)', time: '18:00-22:00', icon: Sunset, color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
] as const;

function CheckpointForm({
    formState,
    setFormState,
    isSaving,
    isEditing,
    onSave,
    onCancel,
    StatusToggle,
}: {
    formState: CheckpointFormState;
    setFormState: React.Dispatch<React.SetStateAction<CheckpointFormState>>;
    isSaving: boolean;
    isEditing: boolean;
    onSave: () => void;
    onCancel: () => void;
    StatusToggle: React.FC<{ active: boolean; onToggle: () => void; disabled?: boolean }>;
}) {
    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                {isEditing ? <Edit size={16} className="text-[#339274]" /> : <Plus size={16} className="text-[#339274]" />}
                <h4 className="text-sm font-semibold text-gray-900">{isEditing ? 'Edit Checkpoint' : 'Add New Checkpoint'}</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Checkpoint Name *</label>
                    <InputText
                        value={formState.name}
                        onChange={(e) => setFormState(prev => ({ ...prev, name: e.target.value ?? '' }))}
                        placeholder="Enter checkpoint name"
                        disabled={isSaving}
                        className="w-full text-sm p-2"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Requirement <span className="text-gray-400">(Optional)</span></label>
                    <InputTextarea
                        value={formState.requirement}
                        onChange={(e) => setFormState(prev => ({ ...prev, requirement: e.target.value ?? '' }))}
                        placeholder="Enter requirement..."
                        rows={2}
                        disabled={isSaving}
                        className="w-full text-sm p-2"
                        autoResize
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Description <span className="text-gray-400">(Optional)</span></label>
                    <InputTextarea
                        value={formState.description}
                        onChange={(e) => setFormState(prev => ({ ...prev, description: e.target.value ?? '' }))}
                        placeholder="Enter description..."
                        rows={2}
                        disabled={isSaving}
                        className="w-full text-sm p-2"
                        autoResize
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Task Type</label>
                    <div className="flex gap-3 flex-wrap">
                        {(['Visual', 'Hearing', 'Manual'] as const).map((type) => (
                            <label key={type} className="inline-flex items-center gap-1.5 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formState.taskType.includes(type)}
                                    onChange={(e) => {
                                        const newTypes = e.target.checked
                                            ? [...formState.taskType, type]
                                            : formState.taskType.filter((t) => t !== type);
                                        setFormState(prev => ({ ...prev, taskType: newTypes }));
                                    }}
                                    className="rounded border-gray-300 text-[#339274] focus:ring-[#339274] w-4 h-4"
                                    disabled={isSaving}
                                />
                                <span className="text-sm text-gray-700">{type}</span>
                            </label>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Input Required</label>
                    <select
                        value={formState.isInputRequired}
                        onChange={(e) => setFormState(prev => ({ ...prev, isInputRequired: e.target.value as CheckpointFormState['isInputRequired'] }))}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#339274] focus:border-transparent"
                        disabled={isSaving}
                    >
                        <option value="None">None</option>
                        <option value="Number">Number</option>
                        <option value="Text">Text</option>
                        <option value="Both">Both</option>
                    </select>
                </div>
            </div>

            <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Execution Shift</label>
                <div className="grid grid-cols-3 gap-2">
                    {SHIFT_OPTIONS.map((shift) => {
                        const isSelected = formState.executionPhase === shift.id;
                        const ShiftIcon = shift.icon;
                        return (
                            <button
                                key={shift.id}
                                type="button"
                                onClick={() => setFormState(prev => ({ ...prev, executionPhase: shift.id, executionTime: shift.time }))}
                                disabled={isSaving}
                                className={`flex items-center gap-1.5 px-2 py-2 rounded-lg text-xs border-2 transition-all ${isSelected
                                    ? `${shift.color} border-current`
                                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                                    } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <ShiftIcon className="w-3.5 h-3.5" />
                                <span className="font-medium truncate">{shift.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={formState.isImageRequired}
                        onChange={(e) => setFormState(prev => ({ ...prev, isImageRequired: e.target.checked }))}
                        className="rounded border-gray-300 text-[#339274] focus:ring-[#339274] w-4 h-4"
                        disabled={isSaving}
                    />
                    <span className="text-sm text-gray-700">Image Required</span>
                </label>
                {isEditing && (
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-medium text-gray-600">Status</label>
                        <div className="flex items-center gap-2 bg-gray-50 px-2 py-1 rounded-lg border border-gray-100">
                            <StatusToggle
                                active={formState.status === 'active'}
                                onToggle={() => setFormState(prev => ({ ...prev, status: prev.status === 'active' ? 'inactive' : 'active' }))}
                                disabled={isSaving}
                            />
                            <span className={`text-xs font-semibold uppercase ${formState.status === 'active' ? 'text-green-600' : 'text-gray-400'}`}>
                                {formState.status}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Frequency *</label>
                    <select
                        value={formState.frequency}
                        onChange={(e) => {
                            const frequency = e.target.value as CheckpointFormState['frequency'];
                            const maxBuffer = getMaxBufferTime(frequency);
                            const currentBuffer = formState.bufferTimeDays ?? 0;
                            const newBuffer = currentBuffer > maxBuffer ? getDefaultBufferTime(frequency) : currentBuffer;
                            setFormState(prev => ({ ...prev, frequency, bufferTimeDays: newBuffer }));
                        }}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#339274]"
                        disabled={isSaving}
                    >
                        {Object.keys(FREQUENCY_COLORS).map((freq) => (
                            <option key={freq} value={freq}>{freq}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Buffer (Days)</label>
                    <select
                        value={formState.bufferTimeDays}
                        onChange={(e) => setFormState(prev => ({ ...prev, bufferTimeDays: parseInt(e.target.value) }))}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#339274]"
                        disabled={isSaving}
                    >
                        {getBufferTimeOptions(formState.frequency).map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <p className="text-[10px] text-gray-500 mt-0.5">Recommended: {getDefaultBufferTime(formState.frequency)} day(s)</p>
                </div>
            </div>

            <div className="flex gap-2 pt-2">
                <Button onClick={onSave} variant="default" size="sm" className="flex-1" disabled={!formState.name.trim() || isSaving}>
                    {isSaving ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
                </Button>
                <Button onClick={onCancel} variant="outline" size="sm" className="flex-1" disabled={isSaving}>Cancel</Button>
            </div>
        </div>
    );
}

export default function ProjectAssetCheckpointDrawer({
    open,
    onClose,
    projectAsset,
    onRefresh
}: ProjectAssetCheckpointDrawerProps) {
    const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
    const [loading, setLoading] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingCheckpointId, setEditingCheckpointId] = useState<string | null>(null);
    const [expandedCheckpointId, setExpandedCheckpointId] = useState<string | null>(null);
    const hasAutoExpandedRejected = useRef(false);
    const [showSaveSuccess, setShowSaveSuccess] = useState(false);
    const [showCreateSuccess, setShowCreateSuccess] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Initial state for forms
    const initialFormState: CheckpointFormState = {
        name: '',
        frequency: 'Weekly',
        description: '',
        requirement: '',
        taskType: [],
        isImageRequired: false,
        isInputRequired: 'None',
        executionPhase: '',
        executionTime: '',
        bufferTimeDays: getDefaultBufferTime('Weekly'),
        status: 'active'
    };

    const [formState, setFormState] = useState<CheckpointFormState>(initialFormState);

    // Reset auto-expand ref when drawer opens
    useEffect(() => {
        if (open && projectAsset) {
            hasAutoExpandedRejected.current = false;
        }
    }, [open, projectAsset]);

    // Fetch checkpoints when drawer opens
    useEffect(() => {
        if (open && projectAsset) {
            fetchCheckpoints();
        }
    }, [open, projectAsset]);

    // Auto-expand first rejected checkpoint when checkpoints load so rejection reason is visible
    useEffect(() => {
        if (!open || !projectAsset || loading || checkpoints.length === 0 || hasAutoExpandedRejected.current) return;
        const rejected = checkpoints.filter((c) => c.approvalStatus === 'rejected');
        if (rejected.length > 0) {
            const firstId = rejected[0]._id || (rejected[0].id as string) || '';
            if (firstId) {
                setExpandedCheckpointId(firstId);
                hasAutoExpandedRejected.current = true;
            }
        }
    }, [open, projectAsset, loading, checkpoints]);

    const fetchCheckpoints = async () => {
        if (!projectAsset) return;

        try {
            setLoading(true);
            const response = await checkpointService.getProjectAssetCheckpoints(projectAsset._id);
            const list = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
            setCheckpoints(list);
        } catch (error) {
            console.error('Failed to fetch checkpoints:', error);
            toast.error('Failed to load checkpoints');
        } finally {
            setLoading(false);
        }
    };

    const handleAddCheckpoint = async () => {
        if (!formState.name.trim() || !projectAsset) {
            toast.error('Please enter checkpoint name');
            return;
        }

        // Validations
        const nameValidation = validateCheckpointName(formState.name);
        if (!nameValidation.valid) {
            toast.error(nameValidation.error || 'Validation failed');
            return;
        }

        if (formState.description) {
            const descValidation = validateDescription(formState.description);
            if (!descValidation.valid) {
                toast.error(descValidation.error || 'Validation failed');
                return;
            }
        }

        if (formState.requirement) {
            const reqValidation = validateRequirement(formState.requirement);
            if (!reqValidation.valid) {
                toast.error(reqValidation.error || 'Validation failed');
                return;
            }
        }

        try {
            setIsSaving(true);
            await checkpointService.createProjectAssetCheckpoint(projectAsset._id, {
                ...formState,
                name: formState.name.trim(),
                taskType: formState.taskType.length > 0 ? formState.taskType : undefined,
                requirement: formState.requirement || undefined,
                description: formState.description || undefined,
                executionPhase: formState.executionPhase || undefined,
                executionTime: formState.executionTime || undefined,
            });

            // Show success animation
            setShowCreateSuccess(true);
            setTimeout(() => setShowCreateSuccess(false), 3000);

            toast.success('Checkpoint created successfully');
            setFormState(initialFormState);
            setShowAddForm(false);
            await fetchCheckpoints();
            if (onRefresh) onRefresh();
        } catch (error: any) {
            console.error('Failed to create checkpoint:', error);
            toast.error(error.response?.data?.message || 'Failed to create checkpoint');
        } finally {
            setIsSaving(false);
        }
    };

    const handleUpdateCheckpoint = async (checkpointId: string) => {
        if (!formState.frequency) {
            toast.error('Please select a frequency');
            return;
        }

        // Validations
        const nameValidation = validateCheckpointName(formState.name);
        if (!nameValidation.valid) {
            toast.error(nameValidation.error || 'Validation failed');
            return;
        }

        try {
            setIsSaving(true);

            // Update the checkpoint data
            await checkpointService.updateProjectAssetCheckpoint(checkpointId, {
                name: formState.name.trim(),
                frequency: formState.frequency,
                description: formState.description || undefined,
                requirement: formState.requirement || undefined,
                taskType: formState.taskType.length > 0 ? formState.taskType : undefined,
                isImageRequired: formState.isImageRequired,
                isInputRequired: formState.isInputRequired,
                executionPhase: formState.executionPhase || undefined,
                executionTime: formState.executionTime || undefined
                // Note: bufferTimeDays might not be supported in updateProjectAssetCheckpoint based on service signature
                // If it is, uncomment below:
                // bufferTimeDays: formState.bufferTimeDays
            });

            // Update status separately if it changed
            const currentCheckpoint = checkpoints.find(c => (c._id === checkpointId || c.id === checkpointId));
            if (currentCheckpoint && currentCheckpoint.status !== formState.status) {
                await checkpointService.toggleProjectAssetCheckpointStatus(checkpointId, formState.status);
            }

            // Show success indicator
            setShowSaveSuccess(true);
            setTimeout(() => setShowSaveSuccess(false), 3000);

            toast.success('Checkpoint updated successfully');
            setEditingCheckpointId(null);
            await fetchCheckpoints();
            if (onRefresh) onRefresh();
        } catch (error: any) {
            console.error('Failed to update checkpoint:', error);
            toast.error(error.response?.data?.message || 'Failed to update checkpoint');
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggleStatus = async (checkpointId: string) => {
        try {
            setLoading(true);
            const checkpoint = checkpoints.find(c => (c._id === checkpointId || c.id === checkpointId));
            if (!checkpoint) return;

            const newStatus = checkpoint.status === 'active' ? 'inactive' : 'active';
            await checkpointService.toggleProjectAssetCheckpointStatus(checkpointId, newStatus);

            toast.success('Status updated successfully');
            await fetchCheckpoints();
            if (onRefresh) onRefresh();
        } catch (error: any) {
            console.error('Failed to toggle status:', error);
            toast.error(error.response?.data?.message || 'Failed to update status');
        } finally {
            setLoading(false);
        }
    };

    const startEditing = (checkpoint: Checkpoint) => {
        const cpId = checkpoint._id || checkpoint.id || '';
        setEditingCheckpointId(cpId);
        setExpandedCheckpointId(cpId);
        setShowAddForm(false);
        setFormState({
            name: checkpoint.name,
            frequency: checkpoint.frequency as any,
            description: checkpoint.description || '',
            requirement: checkpoint.requirement || '',
            taskType: checkpoint.taskType || [],
            isImageRequired: checkpoint.isImageRequired || false,
            isInputRequired: checkpoint.isInputRequired || 'None',
            executionPhase: checkpoint.executionPhase || '',
            executionTime: checkpoint.executionTime || '',
            bufferTimeDays: checkpoint.bufferTimeDays || getDefaultBufferTime(checkpoint.frequency),
            status: checkpoint.status || 'active'
        });
    };

    // Toggle Component for reuse
    const StatusToggle = ({ active, onToggle, disabled }: { active: boolean, onToggle: () => void, disabled?: boolean }) => (
        <button
            onClick={(e) => {
                e.stopPropagation();
                if (!disabled) onToggle();
            }}
            disabled={disabled}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#339274] focus:ring-offset-2 ${active ? 'bg-[#339274]' : 'bg-gray-200'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${active ? 'translate-x-6' : 'translate-x-1'
                    }`}
            />
        </button>
    );

    const cancelEditing = () => {
        setEditingCheckpointId(null);
        setFormState(initialFormState);
    };

    const toggleExpand = (checkpointId: string) => {
        if (expandedCheckpointId === checkpointId) {
            setExpandedCheckpointId(null);
            if (editingCheckpointId === checkpointId) {
                cancelEditing();
            }
        } else {
            setExpandedCheckpointId(checkpointId);
            // Don't auto-edit on expand, only via edit button
        }
    };

    const getFrequencyColor = (frequency: string) => {
        return FREQUENCY_COLORS[frequency] || { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', dot: 'bg-gray-500' };
    };

    const handleAddButtonClick = () => {
        if (showAddForm) {
            setShowAddForm(false);
            setFormState(initialFormState);
        } else {
            setShowAddForm(true);
            setEditingCheckpointId(null);
            setExpandedCheckpointId(null);
            setFormState(initialFormState);
        }
    };

    if (!open || !projectAsset) return null;

    return (
        <AnimatePresence>
            {open && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9999] bg-black/40"
                        onClick={onClose}
                    />

                    {/* Drawer */}
                    <motion.div
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "tween", ease: "easeInOut", duration: 0.3 }}
                        className="fixed inset-y-0 right-0 z-[9999] w-full max-w-4xl overflow-y-auto bg-white shadow-xl"
                    >
                        <div className="flex flex-col h-full">
                            {/* Header */}
                            <div className="flex items-center justify-between gap-4 p-6 border-b border-gray-200">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <button
                                        onClick={onClose}
                                        className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors flex-shrink-0"
                                        aria-label="Close drawer"
                                    >
                                        <ArrowLeft size={20} />
                                    </button>
                                    <div className="w-12 h-12 bg-[#339274]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <Building2 size={24} className="text-[#339274]" />
                                    </div>
                                    <div className="min-w-0">
                                        <h2 className="text-xl font-semibold text-gray-900 truncate">Checkpoints for {projectAsset.name}</h2>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-sm text-gray-600">
                                                {checkpoints.length} checkpoints
                                            </span>
                                            <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                            <span className="text-sm font-medium text-green-600">
                                                {checkpoints.filter(c => c.status === 'active').length} active
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 p-6 overflow-y-auto bg-gray-50">
                                {/* Asset Information Card */}
                                <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm mb-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-5 flex items-center gap-2 border-b border-gray-100 pb-3">
                                        <Building2 size={20} className="text-[#339274]" />
                                        Asset Information
                                    </h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                        <div>
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Asset Name</label>
                                            <p className="text-gray-900 mt-1 font-medium text-base">{projectAsset.name}</p>
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Checkpoints</label>
                                            <p className="text-gray-900 mt-1 font-medium text-base">{checkpoints.length}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Checkpoints List Header */}
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                        <Target size={20} className="text-[#339274]" />
                                        Checkpoints ({checkpoints.length})
                                    </h3>

                                    <Button
                                        onClick={handleAddButtonClick}
                                        variant="default"
                                        size="sm"
                                        className={`inline-flex items-center gap-1.5 transition-all ${showAddForm ? 'bg-gray-700 hover:bg-gray-800' : ''}`}
                                    >
                                        <Plus size={16} className={`transition-transform duration-200 ${showAddForm ? 'rotate-45' : ''}`} />
                                        {showAddForm ? 'Cancel Add' : 'Add Checkpoint'}
                                    </Button>
                                </div>

                                {/* Add Checkpoint Form Container */}
                                <AnimatePresence>
                                    {showAddForm && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: "auto" }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="bg-white border border-[#339274]/30 rounded-xl shadow-md p-4 mb-5 overflow-hidden"
                                        >
                                            <CheckpointForm
                                                formState={formState}
                                                setFormState={setFormState}
                                                isSaving={isSaving}
                                                isEditing={false}
                                                onSave={handleAddCheckpoint}
                                                onCancel={handleAddButtonClick}
                                                StatusToggle={StatusToggle}
                                            />
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Checkpoints List */}
                                {loading && checkpoints.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-20">
                                        <div className="w-10 h-10 border-4 border-gray-200 border-t-[#339274] rounded-full animate-spin mb-4"></div>
                                        <p className="text-gray-500">Loading checkpoints...</p>
                                    </div>
                                ) : checkpoints.length === 0 && !showAddForm ? (
                                    <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Target size={32} className="text-gray-300" />
                                        </div>
                                        <h3 className="text-lg font-medium text-gray-900 mb-1">No checkpoints found</h3>
                                        <p className="text-gray-500 mb-6 max-w-sm mx-auto">This asset doesn't have any checkpoints configured yet.</p>
                                        <Button onClick={handleAddButtonClick} variant="outline" className="mx-auto">
                                            Create First Checkpoint
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-6">
                                        {Object.keys(FREQUENCY_COLORS).map((freq) => {
                                            const freqCheckpoints = checkpoints.filter(c => c.frequency === freq);
                                            if (freqCheckpoints.length === 0) return null;

                                            const freqColors = getFrequencyColor(freq);

                                            return (
                                                <div key={freq}>
                                                    {/* Frequency Header */}
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <span className={`w-3 h-3 rounded-full ${freqColors.dot}`} />
                                                        <span className={`text-sm font-bold ${freqColors.text}`}>{freq}</span>
                                                        <span className="bg-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded-full font-medium">
                                                            {freqCheckpoints.length}
                                                        </span>
                                                    </div>

                                                    <div className="space-y-4">
                                                        {freqCheckpoints.map((checkpoint) => {
                                                            const cpId = checkpoint._id || checkpoint.id || '';
                                                            const isExpanded = expandedCheckpointId === cpId;
                                                            const isEditing = editingCheckpointId === cpId;

                                                            if (isEditing) {
                                                                return (
                                                                    <div key={cpId} className="bg-white border-2 border-[#339274]/50 rounded-xl p-6 shadow-md">
                                                                        <CheckpointForm
                                                                            formState={formState}
                                                                            setFormState={setFormState}
                                                                            isSaving={isSaving}
                                                                            isEditing={true}
                                                                            onSave={() => handleUpdateCheckpoint(cpId)}
                                                                            onCancel={cancelEditing}
                                                                            StatusToggle={StatusToggle}
                                                                        />
                                                                    </div>
                                                                );
                                                            }

                                                            // Check if checkpoint is rejected or modified (changed after rejection)
                                                            const isRejected = checkpoint.approvalStatus === 'rejected';
                                                            const isModified = checkpoint.approvalStatus === 'modified';

                                                            return (
                                                                <div
                                                                    key={cpId}
                                                                    className={`group bg-white rounded-xl border transition-all duration-300 overflow-hidden ${isRejected
                                                                        ? 'border-red-300 bg-red-50/30 shadow-sm ring-1 ring-red-200/50'
                                                                        : isModified
                                                                            ? 'border-amber-300 bg-amber-50/30 shadow-sm ring-1 ring-amber-200/50'
                                                                            : isExpanded
                                                                                ? 'border-[#339274] shadow-md ring-1 ring-[#339274]/20'
                                                                                : 'border-gray-200 hover:border-[#339274]/50 hover:shadow-sm'
                                                                        } ${checkpoint.status === 'inactive' ? 'bg-gray-100/80 border-gray-300 opacity-90' : ''}`}
                                                                >
                                                                    <div
                                                                        className="p-5 cursor-pointer"
                                                                        onClick={() => toggleExpand(cpId)}
                                                                    >
                                                                        <div className="flex items-start gap-4">
                                                                            {/* Status Icon */}
                                                                            <div className="mt-1 flex-shrink-0">
                                                                                <StatusToggle
                                                                                    active={checkpoint.status === 'active'}
                                                                                    onToggle={() => handleToggleStatus(cpId)}
                                                                                    disabled={loading}
                                                                                />
                                                                            </div>

                                                                            <div className="flex-1 min-w-0">
                                                                                <div className="flex items-start justify-between gap-4">
                                                                                    <div>
                                                                                        <h4 className="text-base font-semibold text-gray-900 group-hover:text-[#339274] transition-colors">{checkpoint.name}</h4>
                                                                                        {checkpoint.description && (
                                                                                            <p className="text-sm text-gray-500 mt-1 line-clamp-1">{checkpoint.description}</p>
                                                                                        )}
                                                                                    </div>
                                                                                    <div className="flex items-center gap-3 flex-shrink-0">
                                                                                        {/* Status Badge */}
                                                                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-all ${checkpoint.status === 'active'
                                                                                                ? 'bg-green-50 text-green-700 border-green-200'
                                                                                                : 'bg-gray-200 text-gray-600 border-gray-300'
                                                                                            }`}>
                                                                                            {checkpoint.status === 'active' ? 'Active' : 'Inactive'}
                                                                                        </span>

                                                                                        <button
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                startEditing(checkpoint);
                                                                                            }}
                                                                                            className="p-2 text-gray-400 hover:text-[#339274] hover:bg-[#339274]/10 rounded-lg transition-colors"
                                                                                        >
                                                                                            <Edit size={16} />
                                                                                        </button>
                                                                                    </div>
                                                                                </div>

                                                                                {/* Tags */}
                                                                                <div className="flex flex-wrap gap-2 mt-3">
                                                                                    {/* Approval Status Badge - Show prominently if rejected */}
                                                                                    {checkpoint.approvalStatus === 'rejected' && (
                                                                                        <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold bg-red-100 text-red-800 border-2 border-red-300 shadow-sm">
                                                                                            <XCircle size={12} className="mr-1" /> Rejected by Head
                                                                                        </span>
                                                                                    )}
                                                                                    {/* Changed: was rejected and user updated the checkpoint */}
                                                                                    {checkpoint.approvalStatus === 'modified' && (
                                                                                        <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800 border-2 border-amber-300 shadow-sm">
                                                                                            <Edit size={12} className="mr-1" /> Changed
                                                                                        </span>
                                                                                    )}
                                                                                    {checkpoint.approvalStatus === 'pending' && (
                                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                                                                            <AlertCircle size={10} className="mr-1" /> Pending Approval
                                                                                        </span>
                                                                                    )}
                                                                                    {checkpoint.approvalStatus === 'approved' && (
                                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                                                                            <CheckCircle size={10} className="mr-1" /> Approved
                                                                                        </span>
                                                                                    )}
                                                                                    {checkpoint.taskType?.map(type => (
                                                                                        <span key={type} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                                                                                            <Hand size={10} className="mr-1" /> {type}
                                                                                        </span>
                                                                                    ))}
                                                                                    {checkpoint.isInputRequired && checkpoint.isInputRequired !== 'None' && (
                                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                                                                            <FileText size={10} className="mr-1" /> Input Required ({checkpoint.isInputRequired})
                                                                                        </span>
                                                                                    )}
                                                                                    {checkpoint.isImageRequired && (
                                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-pink-100 text-pink-800 border border-pink-200">
                                                                                            <Image size={10} className="mr-1" /> Image Required
                                                                                        </span>
                                                                                    )}
                                                                                </div>

                                                                                {/* Metadata Row */}
                                                                                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 text-xs text-gray-500">
                                                                                    {checkpoint.executionPhase && (
                                                                                        <div className="flex items-center gap-1.5">
                                                                                            <Calendar size={12} />
                                                                                            <span>Shift: {checkpoint.executionPhase === 'Morning' ? 'Pre-Gen (AM)' : checkpoint.executionPhase === 'Evening' ? 'Post-Gen (PM)' : 'Standard (Day)'}</span>
                                                                                        </div>
                                                                                    )}
                                                                                    {checkpoint.executionTime && (
                                                                                        <div className="flex items-center gap-1.5">
                                                                                            <Timer size={12} />
                                                                                            <span>Time: {checkpoint.executionTime}</span>
                                                                                        </div>
                                                                                    )}
                                                                                    {checkpoint.bufferTimeDays !== undefined && (
                                                                                        <div className="flex items-center gap-1.5">
                                                                                            <Clock size={12} />
                                                                                            <span>Buffer: {checkpoint.bufferTimeDays} days</span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>

                                                                                {/* Created/Updated By */}
                                                                                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400">
                                                                                    <span>
                                                                                        Created by {checkpoint.createdBy?.name || 'System'} • {formatRelativeTime(checkpoint.createdAt)}
                                                                                    </span>
                                                                                    {checkpoint.updatedBy && (
                                                                                        <span>
                                                                                            Updated by {checkpoint.updatedBy.name} • {formatRelativeTime(checkpoint.updatedAt)}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        {/* Expanded Details: Rejection Reason + Requirement */}
                                                                        <AnimatePresence>
                                                                            {isExpanded && (checkpoint.approvalStatus === 'rejected' || checkpoint.approverComments || checkpoint.requirement) && (
                                                                                <motion.div
                                                                                    initial={{ height: 0, opacity: 0 }}
                                                                                    animate={{ height: "auto", opacity: 1 }}
                                                                                    exit={{ height: 0, opacity: 0 }}
                                                                                    className="overflow-hidden border-t border-gray-100 bg-gray-50/50"
                                                                                >
                                                                                    <div className="p-4 space-y-3">
                                                                                        {/* Rejection Reason - Show prominently if rejected */}
                                                                                        {checkpoint.approvalStatus === 'rejected' && (
                                                                                            <div>
                                                                                                <h5 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                                                                                                    <XCircle size={14} />
                                                                                                    Rejection Reason
                                                                                                </h5>
                                                                                                <div className="bg-red-50 p-3 rounded-lg border-2 border-red-200 shadow-sm">
                                                                                                    <p className="text-sm text-red-900 leading-relaxed font-medium">
                                                                                                        {checkpoint.approverComments || 'No reason provided.'}
                                                                                                    </p>
                                                                                                </div>
                                                                                            </div>
                                                                                        )}

                                                                                        {/* Approver Comments for other statuses */}
                                                                                        {checkpoint.approvalStatus !== 'rejected' && checkpoint.approverComments && (
                                                                                            <div>
                                                                                                <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Approver Comments</h5>
                                                                                                <p className="text-sm text-gray-600 leading-relaxed bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                                                                                    {checkpoint.approverComments}
                                                                                                </p>
                                                                                            </div>
                                                                                        )}

                                                                                        {/* Requirement Detail */}
                                                                                        {checkpoint.requirement && (
                                                                                            <div>
                                                                                                <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Detailed Requirement</h5>
                                                                                                <p className="text-sm text-gray-600 leading-relaxed bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                                                                                    {checkpoint.requirement}
                                                                                                </p>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                </motion.div>
                                                                            )}
                                                                        </AnimatePresence>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
